import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import {
  PackageOpen,
  Search,
  Truck,
  CheckCircle2,
  Clock,
  Eye,
  MapPin,
  Calendar,
  Loader2,
  CreditCard,
  User,
  AlertCircle,
  FileText,
  Printer,
} from 'lucide-react'
import { useAdminOrders, useUpdateOrderStatus } from '@/modules/cart/hooks/use-cart'
import { TrackingDialog } from '@/modules/shipping/components/tracking-dialog'
import { CreateShipmentDialog } from '@/modules/shipping/components/create-shipment-dialog'
import { shippingService } from '@/modules/shipping/services/shipping.service'


const ORDER_STATUS_OPTIONS = [
  { value: 'CONFIRMED', label: 'Confirmed', badgeClass: 'bg-purple-500/10 text-purple-500 border-purple-500/20' },
  { value: 'PROCESSING', label: 'Processing', badgeClass: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
  { value: 'SHIPPED', label: 'Shipped (In Transit)', badgeClass: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  { value: 'DELIVERED', label: 'Delivered', badgeClass: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  { value: 'CANCELLED', label: 'Cancelled', badgeClass: 'bg-red-500/10 text-red-500 border-red-500/20' },
]

export function AdminOrdersPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [inspectOrder, setInspectOrder] = useState<any | null>(null)
  const [editStatusOrder, setEditStatusOrder] = useState<any | null>(null)
  const [newStatus, setNewStatus] = useState('PROCESSING')
  const [trackingNumber, setTrackingNumber] = useState('')
  const [updateError, setUpdateError] = useState('')
  const [trackingOrder, setTrackingOrder] = useState<any | null>(null)
  const [shipmentModalOrder, setShipmentModalOrder] = useState<any | null>(null)

  const { data: orders = [], isLoading } = useAdminOrders()
  const updateStatusMutation = useUpdateOrderStatus()

  const openStatusModal = (order: any) => {
    setEditStatusOrder(order)
    setNewStatus(order.orderStatus || order.status || 'PROCESSING')
    setTrackingNumber(order.trackingNumber || '')
    setUpdateError('')
  }

  const handleUpdateStatusSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editStatusOrder) return
    setUpdateError('')

    try {
      await updateStatusMutation.mutateAsync({
        id: editStatusOrder.id,
        status: newStatus,
        trackingNumber: trackingNumber || undefined,
      })
      setEditStatusOrder(null)
      if (inspectOrder?.id === editStatusOrder.id) {
        setInspectOrder((prev: any) => ({ ...prev, orderStatus: newStatus, trackingNumber }))
      }
    } catch (err: any) {
      setUpdateError(err.response?.data?.message || 'Failed to update order status')
    }
  }

  const formatCurrency = (val: any) => {
    if (val === null || val === undefined) return '₹0'
    const num = typeof val === 'number' ? val : Number(String(val).replace(/[^0-9.]/g, ''))
    if (isNaN(num)) return '₹0'
    return `₹${num.toLocaleString('en-IN')}`
  }

  const getStatusBadge = (status?: string) => {
    const s = (status || '').toUpperCase()
    const opt = ORDER_STATUS_OPTIONS.find((o) => o.value === s)
    if (opt) {
      return (
        <Badge className={`${opt.badgeClass} border font-bold text-xs`}>
          {opt.label}
        </Badge>
      )
    }
    return (
      <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 font-bold text-xs">
        {status || 'Processing'}
      </Badge>
    )
  }

  const filteredOrders = orders.filter((order: any) => {
    const currentStatus = (order.orderStatus || order.status || '').toUpperCase()
    const matchStatus = statusFilter === 'ALL' || currentStatus === statusFilter

    const q = search.toLowerCase()
    const matchSearch =
      !search ||
      order.orderNumber?.toLowerCase().includes(q) ||
      order.shippingAddress?.fullName?.toLowerCase().includes(q) ||
      order.shippingAddress?.city?.toLowerCase().includes(q) ||
      order.user?.email?.toLowerCase().includes(q)

    return matchStatus && matchSearch
  })

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-bold tracking-tight">Spare Parts Orders & Dispatch</h1>
            <Badge variant="outline" className="border-primary/30 text-primary">
              Fulfillment Desk
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor incoming customer spare parts orders, update fulfillment states, tracking numbers, and view customer delivery info.
          </p>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <Card className="p-4 rounded-xl border border-border bg-card shadow-xs">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
            <Input
              placeholder="Search by Order #, Customer Name, City, or Email..."
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="w-full sm:w-56">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium"
            >
              <option value="ALL">All Order Statuses</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="PROCESSING">Processing</option>
              <option value="SHIPPED">Shipped</option>
              <option value="DELIVERED">Delivered</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Orders Table */}
      {isLoading ? (
        <div className="p-16 text-center flex flex-col items-center justify-center space-y-3">
          <Loader2 className="size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading orders from database...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-border flex flex-col items-center justify-center space-y-3">
          <PackageOpen className="size-12 text-muted-foreground/40" />
          <h3 className="font-display text-lg font-bold">No Customer Orders Found</h3>
          <p className="text-xs text-muted-foreground max-w-sm">
            {search ? 'Try adjusting your search criteria.' : 'No spare parts orders placed yet by customers.'}
          </p>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-xs">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-muted/40 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Order #</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Items</th>
                <th className="px-4 py-3">Total Amount</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Bigship Delivery</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredOrders.map((order: any) => {
                const itemCount = order.items?.reduce((acc: number, item: any) => acc + item.quantity, 0) || 0
                const currentStatus = order.orderStatus || order.status || 'CONFIRMED'
                const orderDate = new Date(order.createdAt).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })

                return (
                  <tr key={order.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-bold text-foreground">{order.orderNumber}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-0.5">
                        <p className="font-bold text-foreground text-xs">
                          {order.shippingAddress?.fullName || `${order.user?.firstName || ''} ${order.user?.lastName || ''}`}
                        </p>
                        <p className="text-[11px] text-muted-foreground font-mono">
                          {order.shippingAddress?.city}, {order.shippingAddress?.state}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-0.5">
                        <span className="text-xs font-semibold text-foreground">
                          {itemCount} {itemCount === 1 ? 'part' : 'parts'}
                        </span>
                        <p className="text-[11px] text-muted-foreground line-clamp-1 max-w-[200px]">
                          {order.items?.map((it: any) => it.title).join(', ')}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-display font-extrabold text-foreground">
                      {formatCurrency(order.totalAmount)}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {orderDate}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(currentStatus)}
                    </td>
                    <td className="px-4 py-3">
                      {order.awbNumber && !order.awbNumber.startsWith('BS-') ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className="border-blue-500/30 text-blue-500 font-mono text-[11px] flex items-center gap-1 font-bold">
                              <Truck className="size-3" /> {order.awbNumber}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-[11px]">
                            <button
                              type="button"
                              onClick={() => setTrackingOrder(order)}
                              className="text-primary hover:underline font-bold"
                            >
                              Track Live
                            </button>
                            <span className="text-muted-foreground">•</span>
                            <button
                              type="button"
                              onClick={() => window.open(shippingService.getLabelUrl(order.id), '_blank')}
                              className="text-muted-foreground hover:text-foreground font-medium flex items-center gap-0.5"
                            >
                              <Printer className="size-3" /> Label
                            </button>
                          </div>
                        </div>
                      ) : (
                        currentStatus !== 'CANCELLED' ? (
                          <div className="space-y-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs font-bold gap-1 border-primary/30 text-primary hover:bg-primary/10 shadow-2xs"
                              onClick={() => setShipmentModalOrder(order)}
                            >
                              <Truck className="size-3" /> Ship with Bigship
                            </Button>
                            {order.awbNumber?.startsWith('BS-') && (
                              <div className="text-[10px] text-amber-500 font-medium">
                                Unmanifested (Draft)
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Cancelled</span>
                        )
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="font-bold text-xs"
                          onClick={() => openStatusModal(order)}
                        >
                          Update Status
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-muted-foreground hover:text-foreground"
                          onClick={() => setInspectOrder(order)}
                        >
                          <Eye className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Update Order Status Modal */}
      <Dialog open={Boolean(editStatusOrder)} onOpenChange={() => setEditStatusOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-lg font-bold">
              Update Order #{editStatusOrder?.orderNumber}
            </DialogTitle>
          </DialogHeader>

          {updateError && (
            <div className="flex items-center gap-2 p-3 text-xs bg-red-500/10 border border-red-500/20 text-red-500 rounded-lg">
              <AlertCircle className="size-4 shrink-0" />
              <span>{updateError}</span>
            </div>
          )}

          <form onSubmit={handleUpdateStatusSubmit} className="space-y-4 pt-2">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                Fulfillment Status *
              </label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                {ORDER_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                Carrier Tracking Number (Optional)
              </label>
              <Input
                placeholder="e.g. BLUEDART-98421035 or DTDC-IN-482"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
              />
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setEditStatusOrder(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateStatusMutation.isPending}
                className="bg-primary text-white font-bold"
              >
                {updateStatusMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Apply Status
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inspect Order Details Modal */}
      <Dialog open={Boolean(inspectOrder)} onOpenChange={() => setInspectOrder(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold flex items-center justify-between">
              <span>Order #{inspectOrder?.orderNumber}</span>
              {inspectOrder && getStatusBadge(inspectOrder.orderStatus || inspectOrder.status)}
            </DialogTitle>
          </DialogHeader>

          {inspectOrder && (
            <div className="space-y-5 pt-2 text-sm">
              {/* Delivery Details */}
              <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-2">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <MapPin className="size-3.5 text-primary" /> Delivery Destination & Customer Info
                </span>
                <p className="font-bold text-foreground">{inspectOrder.shippingAddress?.fullName}</p>
                <p className="text-xs text-muted-foreground">
                  {inspectOrder.shippingAddress?.addressLine1}
                  {inspectOrder.shippingAddress?.addressLine2 ? `, ${inspectOrder.shippingAddress.addressLine2}` : ''}
                </p>
                <p className="text-xs text-muted-foreground">
                  {inspectOrder.shippingAddress?.city}, {inspectOrder.shippingAddress?.state} - {inspectOrder.shippingAddress?.postalCode}
                </p>
                <p className="text-xs text-muted-foreground">
                  Phone: <strong>{inspectOrder.shippingAddress?.phone}</strong>
                </p>
                {inspectOrder.trackingNumber && (
                  <div className="mt-2 pt-2 border-t border-border/60 text-xs flex items-center gap-1.5">
                    <Truck className="size-3.5 text-blue-500" />
                    <span>Tracking Number: <strong className="font-mono">{inspectOrder.trackingNumber}</strong></span>
                  </div>
                )}
              </div>

              {/* Bigship Logistics Section */}
              {inspectOrder.awbNumber ? (
                <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-blue-500 flex items-center gap-1.5">
                      <Truck className="size-3.5" /> Bigship Logistics Dispatched
                    </span>
                    <Badge className="bg-blue-500 text-white font-mono text-[11px] font-bold">
                      {inspectOrder.courierPartner || 'Delhivery Express Surface'}
                    </Badge>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1 border-t border-blue-500/10">
                    <div>
                      <span className="text-xs text-muted-foreground font-semibold">AWB Number: </span>
                      <span className="font-mono text-sm font-bold text-foreground">{inspectOrder.awbNumber}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        className="h-8 text-xs font-bold bg-primary text-white gap-1"
                        onClick={() => setTrackingOrder(inspectOrder)}
                      >
                        <Truck className="size-3" /> Track Live
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs font-bold gap-1"
                        onClick={() => window.open(shippingService.getLabelUrl(inspectOrder.id), '_blank')}
                      >
                        <Printer className="size-3" /> Print Label
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                (inspectOrder.orderStatus !== 'CANCELLED' && inspectOrder.status !== 'CANCELLED') && (
                  <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="space-y-0.5">
                      <p className="font-bold text-foreground text-xs flex items-center gap-1.5">
                        <Truck className="size-3.5 text-primary" /> Ready for Bigship Dispatch
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        Book courier partner, generate AWB and shipping label PDF.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="bg-primary text-white font-bold text-xs gap-1.5 shrink-0"
                      onClick={() => {
                        const cur = inspectOrder
                        setInspectOrder(null)
                        setShipmentModalOrder(cur)
                      }}
                    >
                      <Truck className="size-3.5" /> Ship via Bigship
                    </Button>
                  </div>
                )
              )}

              {/* Items List */}
              <div className="space-y-3">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Items In Order ({inspectOrder.items?.length})
                </span>
                <div className="divide-y divide-border rounded-xl border border-border p-3">
                  {inspectOrder.items?.map((item: any) => {
                    const itemPrice = typeof item.price === 'number' ? item.price : Number(String(item.price).replace(/[^0-9.]/g, '')) || 0
                    const itemQty = Number(item.quantity) || 1
                    const itemTotal = typeof item.total === 'number'
                      ? item.total
                      : (item.total ? Number(String(item.total).replace(/[^0-9.]/g, '')) : itemPrice * itemQty)

                    return (
                      <div key={item.id} className="py-2.5 first:pt-0 last:pb-0 flex items-center justify-between text-xs">
                        <div>
                          <p className="font-bold text-foreground">{item.title}</p>
                          <p className="text-muted-foreground font-mono">
                            Part #{item.partNumber} • Qty: {itemQty}
                          </p>
                        </div>
                        <span className="font-display font-bold text-foreground">
                          {formatCurrency(itemTotal)}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Payment Info */}
              <div className="rounded-xl border border-border/80 bg-card p-4 space-y-2 text-xs">
                <div className="flex justify-between text-muted-foreground">
                  <span>Payment Method</span>
                  <span className="font-semibold text-foreground">{inspectOrder.paymentMethod || 'Online Verified'}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Payment Status</span>
                  <span className="font-semibold text-emerald-500 font-mono">
                    {inspectOrder.paymentStatus || 'PAID'}
                  </span>
                </div>
                <div className="flex justify-between border-t border-border pt-2 text-sm font-extrabold text-foreground">
                  <span>Total Order Value</span>
                  <span className="font-display text-base text-primary">
                    {formatCurrency(inspectOrder.totalAmount)}
                  </span>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={() => {
                    setInspectOrder(null)
                    openStatusModal(inspectOrder)
                  }}
                  className="font-bold"
                >
                  Change Status
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bigship Live Tracking Modal */}
      <TrackingDialog
        open={Boolean(trackingOrder)}
        onOpenChange={() => setTrackingOrder(null)}
        awbNumber={trackingOrder?.awbNumber}
        orderNumber={trackingOrder?.orderNumber}
        orderId={trackingOrder?.id}
        isAdmin={true}
      />

      {/* Book Shipment on Bigship Dialog */}
      <CreateShipmentDialog
        open={Boolean(shipmentModalOrder)}
        onOpenChange={(open) => {
          if (!open) setShipmentModalOrder(null)
        }}
        order={shipmentModalOrder}
        onSuccess={() => {
          // Orders list auto-refreshes via React Query invalidation in useCreateShipment
          setShipmentModalOrder(null)
        }}
      />

    </div>
  )
}
